
import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogContent, 
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Portfolio } from '@/schemas/portfolio'; // Updated import
import { getAuth } from 'firebase/auth'; // Added for current user

interface MobileAddBusinessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolios: Portfolio[];
  onCreateNewPortfolio: () => void;
  onSelectPortfolio: (portfolioId: string) => void;
}

const MobileAddBusinessModal: React.FC<MobileAddBusinessModalProps> = ({
  open,
  onOpenChange,
  portfolios,
  onCreateNewPortfolio,
  onSelectPortfolio
}) => {
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>("");
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  
  // Reset selection when modal is closed
  useEffect(() => {
    if (!open) {
      // Reset state when modal closes
      const timeoutId = setTimeout(() => {
        setSelectedPortfolioId("");
        setIsProcessingAction(false);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  
  // Filter portfolios to only show those where the user is an owner or primary owner
  const ownedPortfolios = portfolios.filter(portfolio => {
    if (!currentUserId || !portfolio.members) return false;
    return portfolio.members.some(member => 
      member.userId === currentUserId &&
      (member.role?.toLowerCase() === 'owner' || member.role?.toLowerCase() === 'primary owner')
    );
  });
  
  // Debug portfolio data
  useEffect(() => {
    if (open) {
      console.log("MobileAddBusiness - All portfolios:", portfolios.length);
      console.log("MobileAddBusiness - Raw portfolio data:", portfolios.map(p => ({
        id: p.id,
        name: p.name, // Use name
        memberCount: p.members?.length || 0
      })));
      console.log("MobileAddBusiness - Owned portfolios:", ownedPortfolios.length);
      console.log("MobileAddBusiness - Owned portfolio list:", ownedPortfolios.map(p => ({ 
        id: p.id, 
        name: p.name, // Use name
        // Role needs to be derived from members array for current user
      })));
    }
  }, [portfolios, ownedPortfolios, open]);
  
  const handleSelectPortfolio = (value: string) => {
    setSelectedPortfolioId(value);
  };
  
  const handleAddBusiness = () => {
    if (!selectedPortfolioId || isProcessingAction) {
      return;
    }
    
    setIsProcessingAction(true);
    console.log("Add business clicked with portfolio:", selectedPortfolioId);
    
    // Store the selected ID because state updates are asynchronous
    const portfolioToSelect = selectedPortfolioId;
    
    // Close the modal first
    onOpenChange(false);
    
    // Notify parent component about selection after modal closes
    setTimeout(() => {
      onSelectPortfolio(portfolioToSelect);
    }, 300);
  };
  
  const handleCreatePortfolioClick = () => {
    if (isProcessingAction) {
      return;
    }
    
    setIsProcessingAction(true);
    console.log("Create new portfolio clicked from mobile modal");
    
    // Close this modal first
    onOpenChange(false);
    
    // Notify parent about create portfolio action after modal closes
    setTimeout(() => {
      onCreateNewPortfolio();
    }, 300);
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (isProcessingAction && !newOpen) return; // Prevent closing during processing
        onOpenChange(newOpen);
      }}
      modal={true}
    >
      <DialogContent 
        className="sm:max-w-[425px] bg-white"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Add New Business</DialogTitle>
          <DialogDescription>
            {ownedPortfolios.length === 0 
              ? "You need to create a portfolio first" 
              : "Select a portfolio to add a new business"}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-5">
          {ownedPortfolios.length > 0 && (
            <div className="space-y-2">
              <label htmlFor="portfolio-select" className="text-sm font-medium">
                Portfolios You Own
              </label>
              <Select 
                onValueChange={handleSelectPortfolio} 
                value={selectedPortfolioId}
                disabled={isProcessingAction}
              >
                <SelectTrigger id="portfolio-select" className="w-full">
                  <SelectValue placeholder="Select a portfolio" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {ownedPortfolios.map((portfolio) => (
                    <SelectItem key={portfolio.id} value={portfolio.id}>
                      {portfolio.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className={ownedPortfolios.length > 0 ? "flex items-center justify-center" : ""}>
            {ownedPortfolios.length > 0 && <span className="px-4 text-sm text-gray-500">or</span>}
          </div>
          
          <Button
            variant={ownedPortfolios.length === 0 ? "default" : "outline"}
            className="w-full flex items-center justify-center gap-2"
            onClick={handleCreatePortfolioClick}
            disabled={isProcessingAction}
          >
            <Plus size={16} />
            Create New Portfolio
          </Button>
        </div>
        {ownedPortfolios.length > 0 && (
          <DialogFooter>
            <Button 
              className="w-full"
              disabled={!selectedPortfolioId || isProcessingAction}
              onClick={handleAddBusiness}
            >
              Continue
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MobileAddBusinessModal;
